import React from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';
import Cloud from '../../assets/cloud.jpg';

const BRAND_DEEP = '#2c4767';
const BRAND_GOLD = '#b08d57';
const CREAM = '#f7f4ee';
const INK = '#1a2733';
const INK_SOFT = '#5a6675';

const useStyles = makeStyles((theme) => ({
    wrap: {
        minHeight: '100vh',
        background: CREAM,
    },
    hero: {
        position: 'relative',
        backgroundImage: `linear-gradient(135deg, rgba(30, 53, 83, 0.72) 0%, rgba(44, 71, 103, 0.55) 100%), url(${Cloud})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: '#ffffff',
        padding: theme.spacing(12, 3, 8, 3),
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(16, 6, 10, 6),
        },
    },
    heroInner: {
        maxWidth: 880,
        margin: '0 auto',
    },
    heroEyebrow: {
        textTransform: 'uppercase',
        letterSpacing: 4,
        fontSize: 12,
        fontWeight: 600,
        opacity: 0.9,
        marginBottom: theme.spacing(2),
        color: '#e8d9b8',
    },
    heroTitle: {
        fontWeight: 700,
        fontSize: '2.4rem',
        lineHeight: 1.15,
        letterSpacing: '-0.5px',
        [theme.breakpoints.up('md')]: {
            fontSize: '3rem',
        },
    },
    heroDivider: {
        width: 56,
        height: 3,
        background: BRAND_GOLD,
        borderRadius: 2,
        margin: theme.spacing(3, 0),
    },
    heroLead: {
        fontSize: '1.05rem',
        lineHeight: 1.7,
        opacity: 0.94,
    },
    contentWrap: {
        padding: theme.spacing(6, 3),
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(8, 6),
        },
    },
    contentCard: {
        background: '#ffffff',
        padding: theme.spacing(4, 4),
        border: `1px solid rgba(176, 141, 87, 0.18)`,
        borderRadius: 14,
        boxShadow: '0 6px 22px rgba(44, 71, 103, 0.06)',
        marginBottom: theme.spacing(3),
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${BRAND_DEEP} 0%, ${BRAND_GOLD} 100%)`,
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
        },
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(3, 2.5),
        },
    },
    sectionTitle: {
        color: BRAND_DEEP,
        fontWeight: 700,
        fontSize: '1.5rem',
        marginBottom: theme.spacing(2),
    },
    sectionBody: {
        color: INK,
        fontSize: '1rem',
        lineHeight: 1.75,
        whiteSpace: 'pre-line',
    },
    paragraph: {
        marginBottom: theme.spacing(2),
        color: INK,
        fontSize: '1rem',
        lineHeight: 1.75,
    },
    last: {
        marginBottom: 0,
    },
}));

export default function Information() {
    const classes = useStyles();

    return (
        <div className={classes.wrap}>
            <CssBaseline/>

            <div className={classes.hero}>
                <div className={classes.heroInner}>
                    <Typography className={classes.heroEyebrow}>Informácie o aplikácii</Typography>
                    <Typography variant="h1" component="h1" className={classes.heroTitle}>
                        Moderné pohrebníctvo, prvá aplikácia pre pohrebné služby
                    </Typography>
                    <div className={classes.heroDivider}/>
                    <Typography className={classes.heroLead}>
                        Komplexný systém na evidenciu, vybavenie a digitalizáciu pohrebných služieb.
                    </Typography>
                </div>
            </div>

            <Container maxWidth="md" className={classes.contentWrap}>
                <div className={classes.contentCard}>
                    <Typography component="h2" className={classes.sectionTitle}>
                        Čo dokáže?
                    </Typography>
                    <Typography component="p" className={classes.paragraph}>
                        Aplikácia Vás správne prevedie jednotlivými krokmi vybavenia pohrebu zosnulého/ej tak,
                        aby ste mali zaznamenané všetko, čo je z hľadiska zákona potrebné a mali k dispozícii všetky dokumenty,
                        ktoré sú uvedené v zákone o pohrebníctve č. 131/2010 Z. z., podľa ktorého je postavený model našej aplikácie.
                    </Typography>
                    <Typography component="p" className={classes.paragraph}>
                        Aplikácia automaticky generuje splnomocnenie, ktoré viete podpísať digitálne,
                        evidenciu zosnulých, spomienkovú stránku a veľa ďalších vymožeností.
                    </Typography>
                    <Typography component="p" className={classes.paragraph}>
                        Najdôležitejšie dokumenty (List o obhliadke mŕtveho, Úmrtný list, Pas pre mŕtvolu, Povolenie na pochovanie)
                        si viete jednoducho odfotiť alebo zoskenovať a digitálne uložiť v aplikácii. Samozrejmosťou je aj fotka
                        zosnulého a fotka výkopu hrobu.
                    </Typography>
                    <Typography component="p" className={classes.paragraph}>
                        Okrem uchovávania a spravovania dát o zosnulom ponúka aplikácia aj užitočné upozornenia ohľadom nastávajúcich udalostí.
                        Každé ráno Vám príde na váš e-mail upozornenie, čo Vás čaká najbližšie dni. Napríklad vyzdvihnutie nebohého,
                        dátum a čas príchodu rakvy, dátum a čas pohrebu.
                    </Typography>
                    <Typography component="p" className={`${classes.paragraph} ${classes.last}`}>
                        V neposlednom rade aplikácia ponúka možnosť prispôsobenia, konfigurovania a spravovania vnútorných procesov
                        pohrebníctva (služby, produkty a ich ceny).
                    </Typography>
                </div>

                <div className={classes.contentCard}>
                    <Typography component="h2" className={classes.sectionTitle}>
                        Ako je aplikáciu možné používať?
                    </Typography>
                    <Typography component="p" className={`${classes.paragraph} ${classes.last}`}>
                        Aplikácia je prístupná na všetkých mobilných a stolových zariadeniach s prístupom na internet
                        pomocou Vami obľúbeného webového prehliadača.
                    </Typography>
                </div>
            </Container>
        </div>
    );
}
